import {
  isVisibilityOperation,
  type PendingOp,
  type VisibilityOperationKind,
} from '../model/operations';
import type { FailedOperation } from '../state/customizationState';
import type {
  RowActionId,
  RowFailureViewModel,
  RowSpinnerViewModel,
} from './rowTypes';
import { LABEL_KEYS, type LabelKey } from './messages';

export type OperationPresentation = {
  action: RowActionId;
  subject: 'question' | 'section';
  spinnerLabelKey: LabelKey;
  failureMessageKey: LabelKey;
  retryLabelKey: LabelKey;
};

export const OPERATION_PRESENTATION: Record<
  VisibilityOperationKind,
  OperationPresentation
> = {
  hideSection: {
    action: 'hide',
    subject: 'section',
    spinnerLabelKey: LABEL_KEYS.hideSectionSpinnerLabel,
    failureMessageKey: LABEL_KEYS.hideSectionFailedMessage,
    retryLabelKey: LABEL_KEYS.retryHideSectionLabel,
  },
  unhideSection: {
    action: 'unhide',
    subject: 'section',
    spinnerLabelKey: LABEL_KEYS.unhideSectionSpinnerLabel,
    failureMessageKey: LABEL_KEYS.unhideSectionFailedMessage,
    retryLabelKey: LABEL_KEYS.retryUnhideSectionLabel,
  },
  hideQuestion: {
    action: 'hide',
    subject: 'question',
    spinnerLabelKey: LABEL_KEYS.hideQuestionSpinnerLabel,
    failureMessageKey: LABEL_KEYS.hideQuestionFailedMessage,
    retryLabelKey: LABEL_KEYS.retryHideQuestionLabel,
  },
  unhideQuestion: {
    action: 'unhide',
    subject: 'question',
    spinnerLabelKey: LABEL_KEYS.unhideQuestionSpinnerLabel,
    failureMessageKey: LABEL_KEYS.unhideQuestionFailedMessage,
    retryLabelKey: LABEL_KEYS.retryUnhideQuestionLabel,
  },
};

// The interpolation parameter of the spinner and retry labels is named after the subject
export function createSpinner(
  pendingOp: PendingOp | undefined,
  name: string
): RowSpinnerViewModel | null {
  if (!pendingOp || !isVisibilityOperation(pendingOp.kind)) {
    return null;
  }

  const { action, subject, spinnerLabelKey } =
    OPERATION_PRESENTATION[pendingOp.kind];

  return {
    kind: action,
    subject,
    label: { key: spinnerLabelKey, params: { [subject]: name } },
  };
}

export function createFailure(
  failure: FailedOperation | undefined,
  name: string
): RowFailureViewModel | null {
  if (!failure || !isVisibilityOperation(failure.op.kind)) {
    return null;
  }

  const { subject, failureMessageKey, retryLabelKey } =
    OPERATION_PRESENTATION[failure.op.kind];

  return {
    message: { key: failureMessageKey },
    retryLabel: { key: retryLabelKey, params: { [subject]: name } },
  };
}
